import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  User,
  Users,
  Heart,
  FileText,
  UserPlus,
  Camera,
  Plus,
  Minus,
} from "lucide-react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  setDoc,
  doc,
  onSnapshot,
  orderBy,
  limit,
  runTransaction,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "@/firebase/config";
import { compressImage } from "@/utils/imageCompression";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import imageCompression from 'browser-image-compression';
import { getCurrentTerm, Term, getApplicableTermFees } from "@/utils/feeCalculations";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle as DTitle, DialogDescription as DDesc, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Info, Calculator, Eye } from "lucide-react";
import { normalizeDate } from "@/utils/feeCalculations";
import ContentLoader from "../../authentication/ContentLoader";
import ProjectedFeePaymentsView from "./ProjectedFeePaymentsView";

interface StudentRegistrationFormProps {
  onBack: () => void;
  isVolunteer?: boolean;
  volunteerSchoolID?: string;
  volunteerName?: string;
  sessionId?: string;
}

interface Guardian {
  name: string;
  phone: string;
  email: string;
  address: string;
}

interface StudentData {
  registrationNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  gender: string;
  admissionGrade: string;
  className: string;
  currentClassUid: string; 
  dormitory: string;
  studentType?: string; 
  guardians: Guardian[];
  medicalInfo?: string;
  otherInfo?: string;
  submittedBy: string;
  photoUrl: string | null;
  medicalDocs?: string[];
  otherDocs?: string[];
  status: string;
  attendance: number;
  academicYear?: string;
  enrolledSubjects?: string[]; 
  admissionDate?: string; 
  outstandingBalance?: number; 
  ongoing?: boolean; 
  sessionId?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

const StudentRegistrationForm = ({ onBack, isVolunteer, volunteerSchoolID, volunteerName, sessionId }: StudentRegistrationFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [admissionGrade, setAdmissionGrade] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [dormitory, setDormitory] = useState("");
  const [guardians, setGuardians] = useState<Guardian[]>([{ name: "", phone: "", email: "", address: "" }]);
  const [medicalInfo, setMedicalInfo] = useState("");
  const [medicalDocuments, setMedicalDocuments] = useState<File[]>([]);
  const [otherInfo, setOtherInfo] = useState("");
  const [otherDocuments, setOtherDocuments] = useState<File[]>([]);
  const [schoolID, setSchoolID] = useState("");
  const [submittedBy, setSubmittedBy] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [dormitories, setDormitories] = useState<string[]>([]);
  const [dormLoading, setDormLoading] = useState(true);
  const [schoolClasses, setSchoolClasses] = useState<{ name: string; classUid: string; nextClassUid?: string; level?: string }[]>([]); 
  const [classLoading, setClassLoading] = useState(true); 
  const [schoolType, setSchoolType] = useState<string>("");
  const [schoolName, setSchoolName] = useState<string>("");
  const [studentType, setStudentType] = useState<string>(""); 
  const [saving, setSaving] = useState(false);
  const [admissionDate, setAdmissionDate] = useState<string>(new Date().toISOString().split('T')[0]); 

  // Fee Logic States
  const [levelFees, setLevelFees] = useState<any[]>([]);
  const [draftFeesCount, setDraftFeesCount] = useState(0);
  const [calculatedFee, setCalculatedFee] = useState<number | null>(null);
  const [feeStructureLoading, setFeeStructureLoading] = useState(false);
  const [termWarning, setTermWarning] = useState<{ show: boolean, daysRemaining: number, message: string } | null>(null);
  const [activeTerm, setActiveTerm] = useState<Term | null>(null);
  const [terms, setTerms] = useState<Term[]>([]);
  const [feeAssessmentConfig, setFeeAssessmentConfig] = useState<any>(null);
  const [showProjectedFee, setShowProjectedFee] = useState(false);

  // Admission Pattern State
  const [activeAdmissionPattern, setActiveAdmissionPattern] = useState<{ id: string; isActive: boolean; components: any[] } | null>(null);
  const [lastSequenceNumber, setLastSequenceNumber] = useState<number>(0);

  // Validate admin claims OR Volunteer Mode
  useEffect(() => {
    if (isVolunteer && volunteerSchoolID) {
        setSchoolID(volunteerSchoolID);
        setSubmittedBy(`Volunteer: ${volunteerName || 'Unknown'}`);
        setHasValidClaims(true);
        setIsAuthLoading(false);
        // Skip normal auth flow
        return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to register a student.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        setIsAuthLoading(false);
        return;
      }

      setSubmittedBy(user.uid);
      try {
        const token = await getIdTokenResult(user, true);
        const claims: CustomClaims = token.claims as unknown as CustomClaims;
        const affiliations: Affiliation[] = claims.affiliations || [];
        const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
        const claimSchoolID: string | undefined = claims.schoolID;

        const adminAff = defaultAff?.role === "admin" && defaultAff.schoolID
          ? defaultAff
          : affiliations.find(aff => aff.role === "admin" && aff.schoolID);

        if (adminAff && adminAff.schoolID === claimSchoolID) {
          setSchoolID(adminAff.schoolID!);
          setHasValidClaims(true);
        } else {
          throw new Error("Invalid admin claims");
        }
      } catch (error) {
        console.error("Claims validation error:", error);
        toast({
          title: "Missing Permissions",
          description: "You lack permissions to register students.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast, isVolunteer, volunteerSchoolID, volunteerName]);

  // Fetch dormitories
  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    setDormLoading(true);
    const dormsRef = collection(db, `schools/${schoolID}/dorms`);
    const unsubscribe = onSnapshot(dormsRef, (snapshot) => {
      const dormNames = snapshot.docs.map(doc => doc.data().name as string).sort();
      setDormitories(dormNames);
      setDormLoading(false);
    }, (error) => {
      console.error("Error fetching dorms:", error);
      toast({
        title: "Error Loading Dormitories",
        description: "Failed to fetch dormitories.",
        variant: "destructive",
        duration: 5000,
      });
      setDormLoading(false);
    });

    return () => unsubscribe();
  }, [schoolID, isAuthLoading, hasValidClaims, toast]);

  // Fetch school type, fee structure, and terms
  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    const fetchData = async () => {
      setFeeStructureLoading(true);
      try {
        console.log("Fetching registration data for School ID:", schoolID);
        
        // Parallel fetch for efficiency
        const [schoolDocSnap, feeDocSnap, termsSnap, draftFeesSnap, assessmentSnap] = await Promise.all([
            getDocs(query(collection(db, `schools/${schoolID}/schoolDetails`), where("__name__", "==", "main"))),
            getDoc(doc(db, `schools/${schoolID}/deployedFeeStructures/current`)), 
            getDocs(collection(db, `schools/${schoolID}/terms`)),
            getDocs(collection(db, `schools/${schoolID}/feeStructures`)), // Check for drafts
            getDoc(doc(db, `schools/${schoolID}/systemConfig/feeAssessment`))
        ]);

        // 1. School Type
        if (!schoolDocSnap.empty) {
          const schoolData = schoolDocSnap.docs[0].data();
          setSchoolType(schoolData.type || "");
          setSchoolName(schoolData.name || schoolData.schoolName || "");
        }

        // 2. Fee Structure Debugging
        console.log(`[FeeDebug] Checking deployed path: schools/${schoolID}/deployedFeeStructures/current`);
        if (feeDocSnap.exists()) {
             const data = feeDocSnap.data();
             console.log("[FeeDebug] Deployed fee structure FOUND:", JSON.stringify(data, null, 2));
             setLevelFees(data.levelFees || []);
        } else {
             console.error("[FeeDebug] Deployed fee structure NOT FOUND at expected path.");
             setLevelFees([]);
        }

        // 2b. Draft Fees Count
        console.log(`[FeeDebug] Draft fee structures count: ${draftFeesSnap.size}`);
        setDraftFeesCount(draftFeesSnap.size);

        // 3. Terms
        const termsList = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Term));
        setTerms(termsList);
        
        // Determine active term immediately
        const current = getCurrentTerm(termsList, new Date());
        setActiveTerm(current);

        // 4. Assessment Config
        if (assessmentSnap.exists()) {
            setFeeAssessmentConfig(assessmentSnap.data());
        }

      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setFeeStructureLoading(false);
      }
    };

    fetchData();
  }, [schoolID, isAuthLoading, hasValidClaims]);

  // Fetch classes
  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    setClassLoading(true);
    const classesRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribe = onSnapshot(classesRef, (snapshot) => {
      const classData = snapshot.docs.map(doc => ({
        name: doc.data().name as string,
        classUid: doc.id,
        level: doc.data().level as string,
        nextClassUid: doc.data().nextClassUid as string,
      })).sort((a, b) => a.name.localeCompare(b.name));
      setSchoolClasses(classData);
      setClassLoading(false);
    }, (error) => {
      console.error("Error fetching classes:", error);
      toast({
        title: "Error Loading Classes",
        description: "Failed to fetch classes.",
        variant: "destructive",
        duration: 5000,
      });
      setClassLoading(false);
    });

    return () => unsubscribe();
  }, [schoolID, isAuthLoading, hasValidClaims, toast]);

  // Live Fee Calculation & Warning Logic
  useEffect(() => {
    if (!selectedClass && !admissionGrade) {
        setCalculatedFee(null);
        setTermWarning(null);
        return;
    }

    // 1. Find Matching Level Fees
    let matchingLevel = levelFees.find((level: any) => level.feeType === 'uniform' || level.feeType === 'Uniform');
    
    // Try match by Class Name
    if (!matchingLevel && selectedClass) {
        matchingLevel = levelFees.find((level: any) => 
            level.classes && level.classes.some((c: string) => 
                c.toLowerCase() === selectedClass.toLowerCase() ||
                selectedClass.toLowerCase().includes(c.toLowerCase()) ||
                c.toLowerCase().includes(selectedClass.toLowerCase())
            )
        );
    }
    
    // Fallback: Match by Grade/Level
    if (!matchingLevel && admissionGrade) {
        matchingLevel = levelFees.find((level: any) => 
            level.level && typeof level.level === 'string' &&
            (level.level.toLowerCase() === admissionGrade.toLowerCase() ||
             admissionGrade.toLowerCase().includes(level.level.toLowerCase()) ||
             level.level.toLowerCase().includes(admissionGrade.toLowerCase()) ||
             (selectedClass && selectedClass.toLowerCase().includes(level.level.toLowerCase()))
            )
        );
    }

    if (matchingLevel) {
        const isDay = studentType === "Day School";

        // Use centralized utility — handles categories, paidOnce, gov sponsorship, day school natively
        const applicable = getApplicableTermFees(admissionDate, matchingLevel, terms, false, isDay, feeAssessmentConfig);
        
        const startTermObj = getCurrentTerm(terms, new Date(admissionDate));
        const startTermNum = startTermObj ? Number(startTermObj.number) : 1;
        const initialFee = startTermNum === 1 ? applicable.term1 : startTermNum === 2 ? applicable.term2 : applicable.term3;
        
        setCalculatedFee(initialFee);
    } else {
        setCalculatedFee(null);
    }

    // 2. Term Ending Warning Logic
    if (activeTerm && activeTerm.closingDate) {
        const today = new Date();
        const closingDateRaw = activeTerm.closingDate;
        let closing: Date;
        
        if (closingDateRaw instanceof Date) {
            closing = closingDateRaw;
        } else if (typeof closingDateRaw === 'object' && closingDateRaw !== null && 'toDate' in closingDateRaw) {
             closing = (closingDateRaw as any).toDate();
        } else {
             closing = new Date(closingDateRaw as unknown as string);
        }
        
        // Calculate days remaining
        const diffTime = closing.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 0 && diffDays < 30) {
            setTermWarning({
                show: true,
                daysRemaining: diffDays,
                message: `Current term ends in ${diffDays} days. Full term fees may still apply.`
            });
        } else {
            setTermWarning(null);
        }
    }

  }, [selectedClass, admissionGrade, studentType, admissionDate, levelFees, activeTerm, terms, feeAssessmentConfig]);

  // Fetch active admission pattern
  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    const fetchAdmissionPattern = async () => {
      try {
        const patternsQuery = query(
          collection(db, `schools/${schoolID}/admissionPatterns`),
          where("isActive", "==", true),
          limit(1)
        );
        const patternsSnap = await getDocs(patternsQuery);
        
        if (!patternsSnap.empty) {
          const patternData = patternsSnap.docs[0].data();
          const components = patternData.components || [];
          
          setActiveAdmissionPattern({
            id: patternsSnap.docs[0].id,
            isActive: patternData.isActive,
            components: components
          });
          
          // Clear any pre-filled value so placeholder shows
          setRegistrationNumber("");
        } else {
          setActiveAdmissionPattern(null);
        }
      } catch (error) {
        console.error("Error fetching admission pattern:", error);
      }
    };

    fetchAdmissionPattern();
  }, [schoolID, isAuthLoading, hasValidClaims]);

  // Helper function to generate admission number from pattern
  const generateAdmissionNumber = (components: any[], sequenceNum: number): string => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    
    return components.map((comp: any) => {
      switch (comp.type) {
        case 'static':
          return comp.value || '';
        case 'year':
          return comp.format === 'YY' ? String(currentYear).slice(-2) : String(currentYear);
        case 'semester':
          return currentMonth < 7 ? '1' : '2';
        case 'sequence':
          return String(sequenceNum).padStart(comp.length || 4, '0');
        case 'alpha-sequence':
          return 'A'.repeat(comp.length || 2);
        case 'checksum':
          return '0';
        case 'lookup':
          return comp.lookupValues?.[0] || '';
        case 'date-part':
          return String(currentMonth).padStart(2, '0');
        default:
          return '';
      }
    }).join('');
  };

  // Guardian helpers
  const addGuardian = () => setGuardians(g => [...g, { name: "", phone: "", email: "", address: "" }]);
  const removeGuardian = (i: number) => guardians.length > 1 && setGuardians(g => g.filter((_, idx) => idx !== i));
  const updateGuardian = (i: number, field: keyof Guardian, val: string) =>
    setGuardians(g => g.map((v, idx) => (idx === i ? { ...v, [field]: val } : v)));

  // Image compression handler
  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log("❌ [Reg Photo] No file selected");
      return;
    }

    console.log("📷 [Reg Photo] File selected:", file.name, "Size:", (file.size / 1024).toFixed(1), "KB", "Type:", file.type);

    toast({
      title: "Processing Image",
      description: "Optimizing photo for storage...",
      duration: 2000,
    });

    const options = {
      maxSizeMB: 0.5, // Increased from 0.1 - less aggressive compression
      maxWidthOrHeight: 1280,
      useWebWorker: true,
      fileType: 'image/jpeg' as string,
    };

    try {
      console.log("🔄 [Reg Photo] Starting compression...");
      const compressedFile = await imageCompression(file, options);
      console.log("✅ [Reg Photo] Compression complete:", (compressedFile.size / 1024).toFixed(1), "KB");
      
      const fileWithNewName = new File([compressedFile], file.name, {
        type: compressedFile.type,
      });
      
      setProfilePhoto(fileWithNewName);
      console.log("✅ [Reg Photo] profilePhoto state updated");
      
      toast({
        title: "Photo Ready",
        description: `Reduced from ${(file.size / 1024 / 1024).toFixed(1)}MB to ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`,
        duration: 3000,
      });
    } catch (error) {
      console.error("❌ [Reg Photo] Compression error:", error);
      setProfilePhoto(file); // Fallback to original
      console.log("⚠️ [Reg Photo] Using original file as fallback");
      toast({
        title: "Optimization Failed",
        description: "Photo was uploaded without compression.",
        variant: "destructive",
      });
    }
  };

  const triggerCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schoolID || !hasValidClaims || (!isVolunteer && !auth.currentUser)) {
      toast({
        title: "Authentication Error",
        description: "Missing permissions or login.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    // Validate student type for mixed schools
    if (schoolType === "Mixed (Day & Boarding)" && !studentType) {
      toast({
        title: "Student Type Required",
        description: "Please select whether this is a boarding or day school student.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    // Validate dormitory for boarding students
    if (studentType === "Boarding" && !dormitory) {
      toast({
        title: "Dormitory Required",
        description: "Dormitory is required for boarding students.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      setSaving(true);
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const selectedClassData = schoolClasses.find(c => c.name === selectedClass);
      
      if (!selectedClassData) {
          throw new Error("Invalid class selection");
      }

      const studentUid = `student_${Date.now()}`;

      // Upload files first (outside transaction)
      let photoUrl: string | null = null;
      if (profilePhoto) {
        let fileToUpload = profilePhoto;
        try {
            // Compress profile photo (max 1024px, 0.7 quality)
            fileToUpload = await compressImage(profilePhoto, 1024, 0.7);
        } catch (err) {
            console.warn("Image compression failed, using original", err);
        }

        const photoRef = ref(storage, `schools/${schoolID}/students/${studentUid}/photo_${fileToUpload.name}`);
        await uploadBytes(photoRef, fileToUpload);
        photoUrl = await getDownloadURL(photoRef);
      }

      const uploadGroup = async (files: File[], sub: "medical" | "other") => {
        if (files.length === 0) return [];
        const urls: string[] = [];
        for (const file of files) {
          let fileToUpload = file;
          try {
             // Compress documents lightly (max 1600px, 0.6 quality) to keep text readable but file small
             // Only if image. If PDF, compressImage returns original.
             fileToUpload = await compressImage(file, 1600, 0.6);
          } catch(err) {
             console.warn("Doc compression failed", err);
          }

          const fRef = ref(storage, `schools/${schoolID}/students/${studentUid}/${sub}/${fileToUpload.name}`);
          await uploadBytes(fRef, fileToUpload);
          urls.push(await getDownloadURL(fRef));
        }
        return urls;
      };

      const medicalUrls = await uploadGroup(medicalDocuments, "medical");
      const otherUrls = await uploadGroup(otherDocuments, "other");

      // Auto-calculated fee logic
      let initialBalance = calculatedFee || 0;
      
      // TRANSACTION: Generate Reg Number + Save Student
      await runTransaction(db, async (transaction) => {
        let finalRegNumber = registrationNumber;

        // 1. If Auto-Implement is ON, calculate the number now
        if (activeAdmissionPattern && activeAdmissionPattern.isActive) {
          // Read the pattern document to get the latest sequence
          const patternRef = doc(db, `schools/${schoolID}/admissionPatterns`, activeAdmissionPattern.id);
          const patternDoc = await transaction.get(patternRef);
          
          if (!patternDoc.exists()) {
             throw new Error("Pattern document missing!");
          }
          
          const patternData = patternDoc.data();
          let currentSequence = patternData.lastSequenceNumber || 0;

          // Lazy initialization: IF lastSequenceNumber is missing, we must find it
          // NOTE: We cannot run a query inside a transaction if we didn't lock it. 
          // But strict locking on a collection query is hard. 
          // So we do a BEST EFFORT guess if it's 0/missing for the very first time.
          // Ideally, 'AdmissionNumberSetup' should have initialized it.
          // We will increment it safely.
          
          if (currentSequence === 0) {
              // Try to fallback to the pattern's default min value
              const seqComp = activeAdmissionPattern.components.find((c: any) => c.type === 'sequence');
              const minVal = seqComp?.min || 1;
              // If it's truly new, start before min so +1 hits min
              currentSequence = minVal - 1; 
          }

          const nextSequence = currentSequence + 1;
          
          // Generate the string
          finalRegNumber = generateAdmissionNumber(activeAdmissionPattern.components, nextSequence);
          
          // Update the pattern with new sequence
          transaction.update(patternRef, { 
             lastSequenceNumber: nextSequence,
             updatedAt: new Date().toISOString()
          });
        }

        // 2. Check for duplicates (Double check)
        // Note: Querying inside transaction for duplicates helps strictness, 
        // but might require indexing. For now, the sequence lock guarantees uniqueness for auto-gen.
        // For manual input, we accept the risk or rely on prior check.

        const data: StudentData = {
          registrationNumber: finalRegNumber,
          firstName,
          middleName,
          lastName,
          email,
          dateOfBirth,
          gender,
          admissionGrade,
          className: selectedClass,
          currentClassUid: selectedClassData.classUid,
          dormitory,
          ...(schoolType === "Mixed (Day & Boarding)" && studentType && { studentType }),
          guardians,
          ...(medicalInfo ? { medicalInfo } : {}),
          ...(otherInfo ? { otherInfo } : {}),
          submittedBy,
          photoUrl,
          medicalDocs: medicalUrls,
          otherDocs: otherUrls,
          status: "Active",
          attendance: 0,
          academicYear: new Date().getFullYear().toString(),
          enrolledSubjects: [],
          admissionDate,
          outstandingBalance: initialBalance,
          ongoing: false,
          ...(sessionId ? { sessionId } : {}),
        };

        const newStudentRef = doc(collection(db, `schools/${schoolID}/students`), studentUid);
        transaction.set(newStudentRef, {
          ...data,
          studentUid,
          createdAt: new Date().toISOString(),
        });
      });

      // Post-transaction operations (Notifications / Inivites)
      // These don't need to be in the transaction
      if (email) {
          // ... Invitation logic (kept same, moved outside transaction for cleanliness)
          const studentInviteId = doc(collection(db, "invitations")).id;
          await setDoc(doc(db, "invitations", studentInviteId), {
              inviteId: studentInviteId,
              email,
              studentName: `${firstName} ${middleName ? middleName + " " : ""}${lastName}`,
              schoolName,
              schoolID,
              role: "student",
              status: "pending",
              createdAt: new Date().toISOString(),
              invitedBy: submittedBy,
          });
      }
      
      for (const guardian of guardians) {
        if (guardian.email) {
             const guardianInviteId = doc(collection(db, "invitations")).id;
             await setDoc(doc(db, "invitations", guardianInviteId), {
                inviteId: guardianInviteId,
                email: guardian.email,
                guardianName: guardian.name,
                schoolName,
                schoolID,
                role: "guardian",
                status: "pending",
                createdAt: new Date().toISOString(),
                invitedBy: submittedBy,
                studentUid,
             });
        }
      }

      toast({
        title: "Student Registered",
        description: `${firstName} ${lastName} added successfully with all subjects enrolled.`,
        duration: 3000,
      });
      resetForm();
      onBack();
    } catch (error: any) {
      console.error("Registration error:", error);
      toast({
        title: error.code === "permission-denied" ? "Permission Error" : "Registration Failed",
        description: error.code === "permission-denied"
          ? "Insufficient permissions. Ensure your account is admin."
          : error.message || "Unexpected error.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setProfilePhoto(null);
    setRegistrationNumber(""); 
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setEmail("");
    setDateOfBirth("");
    setGender("");
    setAdmissionGrade("");
    setSelectedClass("");
    setDormitory("");
    setGuardians([{ name: "", phone: "", email: "", address: "" }]);
    setMedicalInfo("");
    setMedicalDocuments([]);
    setOtherInfo("");
    setOtherDocuments([]);
  };



  const isLoading = isAuthLoading || !hasValidClaims || dormLoading || classLoading || feeStructureLoading;

  if (showProjectedFee) {
    return (
      <main className="p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 animate-in fade-in">
        <ProjectedFeePaymentsView
          onBack={() => setShowProjectedFee(false)}
          classes={schoolClasses}
          levelFees={levelFees}
          terms={terms}
          schoolType={schoolType}
          studentType={studentType}
          admissionGrade={admissionGrade}
          selectedClass={selectedClass}
          admissionDate={admissionDate}
          schoolID={schoolID}
          isOngoing={false}
          outstandingBalance={0}
          feeAssessmentConfig={feeAssessmentConfig}
        />
      </main>
    );
  }

  return (
    <ContentLoader isLoading={isLoading} message="">
    <main className="p-3 sm:p-6 bg-gray-50 dark:bg-gray-900">
      <div className="mb-6">
        <Button variant="outline" onClick={onBack} className="w-full sm:w-auto mb-4 flex items-center justify-center gap-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">
          <ArrowLeft className="h-4 w-4" />
          Back to Students
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Register New Student</h1>
        <p className="text-gray-600 dark:text-gray-400">Add a new student to the school management system</p>

      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <Card className="bg-white dark:bg-gray-800 shadow-lg border-slate-200 dark:border-slate-800">
          <CardHeader className="bg-blue-50 dark:bg-blue-900/20">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-col items-center space-y-4 pb-6 border-b border-gray-200 dark:border-gray-700">
              <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                {profilePhoto ? (
                  <img src={URL.createObjectURL(profilePhoto)} alt="profile" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <Camera className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                )}
              </div>
              <div>
                <Label htmlFor="profilePhoto" className="text-gray-700 dark:text-gray-300">Profile Photo</Label>
                <div className="flex flex-col sm:flex-row gap-2 mt-1">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={triggerCamera}
                    className="w-full sm:flex-1 flex items-center justify-center gap-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm"
                  >
                    <Camera className="h-4 w-4" />
                    Take Photo / Choose
                  </Button>
                  {profilePhoto && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      onClick={() => setProfilePhoto(null)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <input
                  id="profilePhoto"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                <p className="text-[10px] text-gray-500 mt-1">Photos are optimized to save space while maintaining quality.</p>
              </div>
            </div>

            <div>
              <Label htmlFor="registrationNumber" className="text-gray-700 dark:text-gray-300">Registration Number *</Label>
              <Input
                id="registrationNumber"
                value={activeAdmissionPattern && activeAdmissionPattern.isActive ? "" : registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                required={!activeAdmissionPattern || !activeAdmissionPattern.isActive}
                disabled={!!(activeAdmissionPattern && activeAdmissionPattern.isActive)}
                placeholder={activeAdmissionPattern && activeAdmissionPattern.isActive ? "Auto-assigned on submit" : "Enter number manually"}
                className={`bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-mono ${
                    activeAdmissionPattern && activeAdmissionPattern.isActive ? "italic text-muted-foreground bg-slate-50 dark:bg-slate-800" : ""
                }`}
              />
              {activeAdmissionPattern && activeAdmissionPattern.isActive && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      System will assign the next sequential number automatically.
                  </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-gray-700 dark:text-gray-300">First Name *</Label>
                <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white" />
              </div>
              <div>
                <Label htmlFor="middleName" className="text-gray-700 dark:text-gray-300">Middle Name</Label>
                <Input id="middleName" value={middleName} onChange={(e) => setMiddleName(e.target.value)} className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white" />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-gray-700 dark:text-gray-300">Last Name *</Label>
                <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white" />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="dateOfBirth" className="text-gray-700 dark:text-gray-300">Date of Birth *</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  required
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <Label htmlFor="admissionDate" className="text-gray-700 dark:text-gray-300">Admission Date *</Label>
                <Input
                  id="admissionDate"
                  type="date"
                  value={admissionDate}
                  onChange={(e) => setAdmissionDate(e.target.value)}
                  required
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Date student joined the school</p>
              </div>
              <div>
                <Label htmlFor="gender" className="text-gray-700 dark:text-gray-300">Gender *</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectItem value="Male" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">Male</SelectItem>
                    <SelectItem value="Female" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="admissionGrade" className="text-gray-700 dark:text-gray-300">Admission Grade *</Label>
                <Select value={admissionGrade} onValueChange={setAdmissionGrade}>
                  <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue placeholder="Select admission grade" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    {["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D+", "D", "F"].map((g) => (
                      <SelectItem key={g} value={g} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="class" className="text-gray-700 dark:text-gray-300">Class *</Label>
                <Select value={selectedClass} onValueChange={setSelectedClass} disabled={classLoading}>
                  <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue placeholder={classLoading ? "Loading classes..." : "Select class"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    {schoolClasses.map((cls) => <SelectItem key={cls.classUid} value={cls.name} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">{cls.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {schoolType === "Mixed (Day & Boarding)" && (
                <div>
                  <Label htmlFor="studentType" className="text-gray-700 dark:text-gray-300">Student Type *</Label>
                  <Select value={studentType} onValueChange={setStudentType}>
                    <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                      <SelectValue placeholder="Select student type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                      <SelectItem value="Boarding" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">Boarding Student</SelectItem>
                      <SelectItem value="Day School" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">Day School Student</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="dormitory" className="text-gray-700 dark:text-gray-300">Dormitory {schoolType === "Mixed (Day & Boarding)" && studentType === "Boarding" ? "*" : schoolType === "Boarding School" ? "*" : ""}</Label>
                <Select 
                  value={dormitory} 
                  onValueChange={setDormitory} 
                  disabled={dormLoading || (schoolType === "Mixed (Day & Boarding)" && studentType === "Day School")}
                >
                  <SelectTrigger className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    <SelectValue placeholder={dormLoading ? "Loading dormitories..." : "Select dormitory"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                    {dormitories.map((d) => (
                      <SelectItem key={d} value={d} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Projected Fee Payment Button */}
            {selectedClass && calculatedFee !== null && (
              (schoolType === "Mixed (Day & Boarding)" ? studentType : true)
            ) && (
              <div className="pt-2">
                <Button
                  type="button"
                  onClick={() => setShowProjectedFee(true)}
                  variant="outline"
                  className="w-full sm:w-auto flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 font-semibold transition-all"
                >
                  <Eye className="h-4 w-4" />
                  Projected Fee Payment
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Financial Preview Section */}
        {selectedClass && (
             <Card className="bg-white dark:bg-gray-800 shadow-lg border-slate-200 dark:border-slate-800 animate-in fade-in slide-in-from-top-4 my-6">
               <CardHeader className="bg-blue-50 dark:bg-blue-900/20">
                 <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
                   <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                   Financial Preview
                 </CardTitle>
               </CardHeader>
               <CardContent className="p-6 space-y-4">
                 {/* Term Warning Banner */}
                 {termWarning && termWarning.show && (
                   <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900">
                     <AlertTriangle className="h-4 w-4" />
                     <AlertTitle>Term Ends Soon</AlertTitle>
                     <AlertDescription>
                       {termWarning.message}
                     </AlertDescription>
                   </Alert>
                 )}

                 {/* Fee Display */}
                 <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-4 rounded-lg border ${calculatedFee !== null ? 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'}`}>
                   <div>
                     <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Estimated Initial Billing</p>
                     <p className="text-xs text-gray-400 dark:text-gray-500">Based on admission date: {admissionDate}</p>
                         {calculatedFee === null && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium">
                            {levelFees.length === 0 
                              ? (draftFeesCount > 0 ? "Fee structures exist but are NOT DEPLOYED. Please Publish them in Finance." : "No fee structure deployed.")
                              : "No fee structure found for this class."}
                        </p>
                     )}
                   </div>
                   <div className="sm:text-right">
                     <p className={`text-2xl font-bold ${calculatedFee !== null ? 'text-gray-900 dark:text-white' : 'text-amber-600 dark:text-amber-500'}`}>
                       {calculatedFee !== null ? `KES ${calculatedFee.toLocaleString()}` : '---'}
                     </p>
                     {calculatedFee !== null && (
                         <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                           {activeTerm ? `Pro-rated for Term ${activeTerm.number}` : 'Pro-rated'}
                         </span>
                     )}
                   </div>
                 </div>
               </CardContent>
             </Card>
        )}

        <Card className="bg-white dark:bg-gray-800 shadow-lg border-slate-200 dark:border-slate-800">
          <CardHeader className="bg-green-50 dark:bg-green-900/20">
            <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-gray-900 dark:text-white">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span>Parent/Guardian Information</span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addGuardian} className="w-full sm:w-auto flex items-center justify-center gap-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">
                <Plus className="h-4 w-4" />
                Add Guardian
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {guardians.map((g, i) => (
              <div key={i} className="space-y-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900 dark:text-white">Guardian {i + 1}</h4>
                  {guardians.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeGuardian(i)}
                      className="flex items-center gap-1 text-red-600 hover:text-red-700"
                    >
                      <Minus className="h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`guardianName-${i}`} className="text-gray-700 dark:text-gray-300">Parent/Guardian Name *</Label>
                    <Input id={`guardianName-${i}`} value={g.name} onChange={(e) => updateGuardian(i, "name", e.target.value)} required className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white" />
                  </div>
                  <div>
                    <Label htmlFor={`guardianPhone-${i}`} className="text-gray-700 dark:text-gray-300">Phone Number *</Label>
                    <Input id={`guardianPhone-${i}`} value={g.phone} onChange={(e) => updateGuardian(i, "phone", e.target.value)} required className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white" />
                  </div>
                </div>

                <div>
                  <Label htmlFor={`guardianEmail-${i}`} className="text-gray-700 dark:text-gray-300">Email Address *</Label>
                  <Input id={`guardianEmail-${i}`} type="email" value={g.email} onChange={(e) => updateGuardian(i, "email", e.target.value)} required className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white" />
                </div>

                <div>
                  <Label htmlFor={`guardianAddress-${i}`} className="text-gray-700 dark:text-gray-300">Address *</Label>
                  <Textarea id={`guardianAddress-${i}`} value={g.address} onChange={(e) => updateGuardian(i, "address", e.target.value)} required rows={3} className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 shadow-lg border-slate-200 dark:border-slate-800">
          <CardHeader className="bg-red-50 dark:bg-red-900/20">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Heart className="h-5 w-5" />
              Medical Information (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label htmlFor="medicalInfo" className="text-gray-700 dark:text-gray-300">Medical Notes</Label>
              <Textarea
                id="medicalInfo"
                value={medicalInfo}
                onChange={(e) => setMedicalInfo(e.target.value)}
                rows={3}
                placeholder="Any medical conditions, allergies, or special requirements..."
                className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <Label htmlFor="medicalDocs" className="text-gray-700 dark:text-gray-300">Medical Documents</Label>
              <Input
                id="medicalDocs"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setMedicalDocuments(Array.from(e.target.files || []))}
                className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload medical records, vaccination certificates, etc.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 shadow-lg border-slate-200 dark:border-slate-800">
          <CardHeader className="bg-purple-50 dark:bg-purple-900/20">
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <FileText className="h-5 w-5" />
              Other Documents (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <Label htmlFor="otherInfo" className="text-gray-700 dark:text-gray-300">Additional Notes</Label>
              <Textarea
                id="otherInfo"
                value={otherInfo}
                onChange={(e) => setOtherInfo(e.target.value)}
                rows={3}
                placeholder="Any additional information about the student..."
                className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <Label htmlFor="otherDocs" className="text-gray-700 dark:text-gray-300">Supporting Documents</Label>
              <Input
                id="otherDocs"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setOtherDocuments(Array.from(e.target.files || []))}
                className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Birth certificate, previous school records, etc.</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onBack} className="w-full sm:w-auto bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isAuthLoading || saving || !schoolID || !hasValidClaims || dormLoading || classLoading}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Register Student"}
          </Button>
        </div>
      </form>



    </main>
    </ContentLoader>
  );
};

export default StudentRegistrationForm;